import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PatientVisit } from '../amplify/api-gateway/search-patients';

dayjs.extend(utc);
dayjs.extend(timezone);

export const TIME_SCALE_MAP: any = {
    d: "days ago",
    w: "weeks ago",
};

export const TIME_SCALE_SHORT_MAP: any = {
    m: "min",
    h: "hr",
    d: "days",
    w: "weeks",
};

/**
 * Gets the day of week as a number, where 0 = Monday ... 6 = Sunday
 */
export const getDayOfWeekUTC = (dt: Date): number => {
    const dayOfWeekIndex = dt.getUTCDay(); // Normally this will return 0 = Sunday ... 6 = Sat
    return (dayOfWeekIndex - 1) % 7;
};

export const getUtcOffsetInHours = (): number => {
    const date = new Date();
    const offsetInMinutes = date.getTimezoneOffset();
    const offsetInHours = -offsetInMinutes / 60; // Negative because getTimezoneOffset returns the opposite
    return offsetInHours;
}

/**
 * Adds a specified number of weeks 
 */
export function addWeeks(date: Date, weeks: number) {
    return new Date(date.getTime() + weeks * 7 * 24 * 60 * 60 * 1000);
}

/**
 * Subtracts a specified number of hours from the input date
 */
export function subtractHours(date: Date, offsetHours: number) {
    date.setHours(date.getHours() - offsetHours);
    return date;
}

/**
 * Gets the absolute time from a relative time.
 * @param relativeDate A string in the form "1d" or "10h"
 * @return The Date object representing the absolute time
 */
export function getAbsoluteTimeFromRelativeTime(relativeDate: string): Date {
    let d = new Date();

    // Get the offset (and convert to number)
    const offset = +getRelativeValue(relativeDate);
    switch (getRelativeScale(relativeDate)) {
        case "s":
            d.setSeconds(d.getSeconds() - offset);
            break;
        case "m":
            d.setMinutes(d.getMinutes() - offset);
            break;
        case "h":
            d.setHours(d.getHours() - offset);
            break;
        case "d":
            d.setHours(d.getHours() - offset * 24);
            break;
        case "w":
            d.setHours(d.getHours() - offset * 24 * 7);
            break;
    }
    return d;
}

/**
 * Gets the value from a relative date string. e.g. "10" from "10h"
 * @param relativeDate A string in the form "1d" or "10h"
 */
export function getRelativeValue(relativeDate: string): number {
    return parseFloat(relativeDate.substring(0, relativeDate.length - 1));
}

/**
 * Gets the scale from a relative date string. e.g. "h" from "10h"
 */
export function getRelativeScale(relativeDate: string): string {
    return relativeDate.substring(relativeDate.length - 1, relativeDate.length);
}

/**
 * Gets the 24-hour time from a Date object
 * @param d The date object
 * @return e.g. 13:42
 */
export function getTimeStrFromDate(d: Date): string {
    if (d) {
        return d.toISOString().substring(11, 16);
    } else {
        return "00:00";
    }
}

/**
 * Gets the date from a Date object
 * @param d The date object
 * @return e.g. 2022-04-23
 */
export function getDateStrFromDate(d?: Date): string {
    if (d) {
        return d.toISOString().substring(0, 10);
    } else {
        return "";
    }
}

/**
 * Gets the 24-hour time from a Date object expressed in the local timezone
 * @param d The date object
 * @return e.g. 13:42
 */
export function getLocalTimeStrFromDate(d: Date): string {
    return d.toLocaleString('sv').replace(' ', 'T').substring(11, 16);
}

/**
 * Gets the date from a Date object expressed in the local timezone
 * @param d The date object
 * @return e.g. 2022-04-23
 */
export function getLocalDateStrFromDate(d: Date): string {
    return d.toLocaleString('sv').replace(' ', 'T').substring(0, 10);
}

/**
 * Gets the date from a Date string expressed in the local timezone
 * @param d The date object as a string
 * @return e.g. 2022-04-23 00:00:00
 */
export function getLocalDateTimeStr(d: string): string {
    if (!d.endsWith('Z') && !d.includes('+')) {
        // Treat it as UTC explicitly
        d = d + 'Z';
    }
    return `${getLocalDateStrFromDate(new Date(d))} ${getLocalTimeStrFromDate(new Date(d))}`;
}

/**
 * Gets the SQL date format from a Date object
 * @param d The date object as a string or epoch timestamp in milliseconds
 * @return e.g. 2022-04-23 00:12:00
 */
export function getSqlDateStrFromDateStr(d: string | number | Date): string {
    // 2011-10-05T14:48:00.000Z
    let isoDate = new Date(d).toISOString();
    isoDate = isoDate.replace("T", " ")
    isoDate = isoDate.substring(0, isoDate.lastIndexOf("."));
    return isoDate;
}

/**
 * Gets the second-level date format from a Date object
 * @param d The date object as a string or epoch timestamp in milliseconds
 * @return e.g. 12/19/2012, 7:00:00 PM
 */
export function getSecondDateStrFromDateStr(d: string | number): string {
    // 2011-10-05T14:48:00.000Z
    const dt = new Date(d);
    if (dt instanceof Date && !isNaN(dt.getTime())) {
        const isoDate = new Date(d).toLocaleString("en-US")
        return isoDate
    } else {
        return "";
    }
}

/**
 * Gets the quantified path from a Date object
 * @param d The date object
 * @return e.g. year=2022/month=03/day=04/hour=05/minute=55
 */
export function getDatePath(d: Date): string {
    // 2011-10-05T14:48:00.000Z
    const isoDate = new Date(d).toISOString();
    const date = isoDate.split("T")[0].split("-");
    const time = isoDate.split("T")[1].split(":");
    return `year=${date[0]}/month=${date[1]}/day=${date[2]}/hour=${time[0]}/minute=${time[1]}`;
}

/**
 * Gets a Date object based on the input
 * @param dateStr e.g. 2022-04-10
 * @param timeStr e.g. 14:23 
 * @returns Date with timezone
 */
export function getLocalizedDate(dateStr: string, timeStr: string): Date {
    const d = new Date(`${dateStr} ${timeStr}`);
    return withTimezone(d);
}

/**
 * Applies a timezone onto a Date object
 * @param date date object
 * @param timezone e.g. America/Vancouver 
 * @returns Date with timezone
 */
export function withTimezone(date: Date, inputTimezone?: string): Date {
    const timezone = inputTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    var invDate = new Date(date.toLocaleString('en-US', {
        timeZone: timezone
    }));
    var diff = date.getTime() - invDate.getTime();
    return new Date(date.getTime() - diff);
}

/**
 * Format the date to Pacific timezone using Intl.DateTimeFormat
 * @param utcDate 
 * @returns date like 2024-11-20
 */
export function getPacificDateString(utcDate: Date): string {
    const pacificDateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  
    // Get the formatted date parts
    const parts = pacificDateFormatter.formatToParts(utcDate);
  
    // Extract year, month, and day from the parts
    const year = parts.find(part => part.type === 'year')?.value;
    const month = parts.find(part => part.type === 'month')?.value;
    const day = parts.find(part => part.type === 'day')?.value;
  
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  
    throw new Error('Failed to format date');
}

/**
 * Generates a list of dates between two dates
 */
export function generateDates(start: Date, end: Date): Date[] {
     // Ensure the end date is inclusive by adding one day's worth of milliseconds.
     const dates: Date[] = [];
     let d = new Date(start);
     while (d <= end) {
         dates.push(d);
         d = new Date(d);
         d.setDate(d.getDate() + 1);
     }
    return dates;
}

/**
 * Gets the earliest time with respect to a patient visit
 */
export function getEarliestTime(date: PatientVisit): number {
    const times = [];
    if (date.roomed_time) {
        times.push(new Date(date.roomed_time).getTime());
    }
    if (date.arrival_time) {
        times.push(new Date(date.arrival_time).getTime());
    }
    if (date.departure_time) {
        times.push(new Date(date.departure_time).getTime());
    }
    if (date.dispo_time) {
        times.push(new Date(date.dispo_time).getTime());
    }
    return Math.min(...times);
}

/**
 * Gets the latest time with respect to a patient visit
 * (current time if there's no departure/dispo)
 */
export function getLatestTime(date: PatientVisit, tenant?: string): number {
    const times = [];
    if (date.departure_time) {
        times.push(new Date(date.departure_time).getTime());
    }
    // We purposefully don't use dispo time as the latest as patient could be 
    // disposed but still in the ED receiving datapoints.
    if (times.length === 0) {
        // The demo uses de-ID data with departure dates possibly in the future
        // so we make sure to simulate a departure date (12 hours after arrival) to 
        // avoid screwing with the an unbounded time range query
        if (tenant === "public") {
            if (date.dispo_time) {
                return new Date(date.dispo_time).getTime() + 1 * 60 * 60 * 1000;
            } else {
                return new Date(date.arrival_time).getTime() + 12 * 60 * 60 * 1000;
            }
        }
        return new Date().getTime();
    } else {
        return Math.max(...times);
    }
}

/**
 * Formats the date that would appear in the date selector
 */
export function formatCustomDateText(filterDateOptions: any, hasShortCut?: boolean): string {
    if (filterDateOptions.dateOptionType === "all") {
        return "Custom";
    } else if (filterDateOptions.dateOptionType === "absolute") {
        if (getDateStrFromDate(filterDateOptions.absoluteStart) === getDateStrFromDate(filterDateOptions.absoluteEnd)) {
            return `${getLocalDateStrFromDate(filterDateOptions.absoluteStart)} ${getLocalTimeStrFromDate(filterDateOptions.absoluteStart)} to ${getLocalTimeStrFromDate(filterDateOptions.absoluteEnd)}`;
        } else {
            return `${getLocalDateStrFromDate(filterDateOptions.absoluteStart)} ${getLocalTimeStrFromDate(filterDateOptions.absoluteStart)} to ${getLocalDateStrFromDate(filterDateOptions.absoluteEnd)} ${getLocalTimeStrFromDate(filterDateOptions.absoluteEnd)}`;
        }
    } else {
        if (hasShortCut && [1, 3, 6, 24].includes(filterDateOptions.relativeStart) && filterDateOptions.relativeEnd === 0 && filterDateOptions.relativeStartScale === "h") {
            // Since shortcut would be enabled
            return "Custom";
        } else if (filterDateOptions.relativeEnd === 0) {
            return `${filterDateOptions.relativeStart} ${TIME_SCALE_SHORT_MAP[filterDateOptions.relativeStartScale]} ago`;
        } else {
            return `${filterDateOptions.relativeStart} ${TIME_SCALE_SHORT_MAP[filterDateOptions.relativeStartScale]} to ${filterDateOptions.relativeEnd} ${TIME_SCALE_SHORT_MAP[filterDateOptions.relativeEndScale]} ago`;
        }
    }
};

/**
 * Formats the date into a user-friendly format
 */
export function getUserFriendlyDate(date: number): string {
    const delta = Math.round((+new Date - date) / 1000);

    const minute = 60,
        hour = minute * 60,
        day = hour * 24,
        week = day * 7;

    let fuzzy: string;

    if (delta < 30) {
        fuzzy = 'Just now';
    } else if (delta < minute) {
        fuzzy = delta + ' seconds ago';
    } else if (delta < 2 * minute) {
        fuzzy = 'A minute ago'
    } else if (delta < hour) {
        fuzzy = Math.floor(delta / minute) + ' min ago';
    } else {
        fuzzy = dayjs(date).format('YYYY-MM-DD HH:mm')
    }
    return fuzzy;
}

/**
 * Formats the date into a user-friendly format like 3/5/2024, 8:57:00 PM
 */
export function getUserFriendlyDateStringWithoutSecond(date: Date): string {
    return date.toLocaleString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour12: true,
      });
}

/**
 * Formats the date into a relative format
 */
export function getRelativeDate(date: Date, anchor?: Date): string {
    const now = anchor ?? new Date();

    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    // Convert both dates to midnight for calendar-based comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thatDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const dayDifference = Math.floor(
        (today.getTime() - thatDay.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (seconds < 60) {
        return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
    } else if (minutes < 60) {
        return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (hours < 24 && dayDifference === 0) {
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else if (dayDifference === 1) {
        return 'yesterday';
    } else if (dayDifference < 30) {
        return `${dayDifference} day${dayDifference === 1 ? '' : 's'} ago`;
    } else if (dayDifference < 365) {
        const months = Math.floor(dayDifference / 30);
        return `${months} month${months === 1 ? '' : 's'} ago`;
    } else {
        const years = Math.floor(dayDifference / 365);
        const months = Math.floor((dayDifference % 365) / 30);

        if (years === 1) {
            // Between 1 and 2 years: show in total months
            const totalMonths = Math.floor(dayDifference / 30);
            return `${totalMonths} month${totalMonths === 1 ? '' : 's'} ago`;
        } else {
            // 2+ years: show years
            return `${years} year${years === 1 ? '' : 's'} ago`;
        }
    }
}

/**
 * Formats the date into a relative format but the specific time after an hour
 */
export function getRelativeDateWithTimeAfterHour(date: Date): string {
    const now = new Date();
    const timeDifference = now.getTime() - date.getTime();
    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) {
        return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
    } else if (minutes < 60) {
        return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else {
        return `${getLocalDateStrFromDate(date)} ${getLocalTimeStrFromDate(date)}`;
    }
}

/**
 * Returns the date difference in a user-friendly format
 */
export function getUserFriendlyDateDifference(dateStr1: string, dateStr2: string) {
    if (!dateStr1 || !dateStr2) {
        return "unknown";
    }

    const date1 = new Date(dateStr1);
    const date2 = new Date(dateStr2);

    const timeDiff = Math.round((date2.getTime() - date1.getTime()) / 1000);
    if (timeDiff < 60) {
        return "< 1 min";
    } else if (timeDiff < 60 * 60) {
        return `${Math.round(timeDiff / 60)} min`
    } else {
        return `${Math.round(timeDiff / (60 * 60))} hours`
    }
}

/**
 * Returns the epoch time, truncated to the minute
 * @param epoch epoch time in seconds
 * @returns truncated epoch time
 */
export function getMinuteTruncatedEpoch(epoch: number) {
    let t = Math.floor(epoch);
    return t - t % 60
}

/**
 * Gets the day truncated time without the minute component
 */
export function getDayTruncatedDate(date: Date) {
    return new Date(date.toDateString());
}

/**
 * Returns the hour minute difference like 03:15
 */
export function getHourMinuteDifference(date1: Date, date2: Date): string {
    // Calculate the time difference in milliseconds
    const timeDifference = Math.abs(date2.getTime() - date1.getTime());
  
    return getStopWatchFormat(timeDifference);
}

/**
 * Returns the hour minute difference like 03:15 given time difference in milli
 */
export function getStopWatchFormat(timeDifference: number): string {
    // Calculate hours and minutes
    const hours = Math.floor(timeDifference / (1000 * 60 * 60));
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
  
    // Format the result as HH:MM
    const formattedResult = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  
    return formattedResult;
}

/**
 * Gets a user readable date
 * @param date 
 * @returns a date like "Fri May 24, 2024, 6:00:00 am"
 */
export function formatUserReadableDate(date: Date, useSeconds: boolean = true): string {
    if (!date) {
        return "";
    }
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: useSeconds ? 'numeric' : undefined,
        hour12: true
    };

    return date.toLocaleDateString('en-US', options);
}

export function getDaysBetweenDates(date1: string | Date, date2: string | Date): number {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
  
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
}

/**
 * Parse date time string as UTC, regardless of the timezone is specified
 */
export function parseAsUTC(dateStr: string): Date {
  // If dateStr has no timezone info, append 'Z' to treat as UTC
  const normalized = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr.replace(' ', 'T'))
    ? dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)
      ? dateStr
      : dateStr + 'Z'
    : dateStr;

  return new Date(normalized);
}

export function parseAsPacific(dateStr: string): Date {
    // Normalize space to 'T'
    const normalized = dateStr.replace(' ', 'T');

    // Parse in America/Los_Angeles timezone
    const dt = dayjs.tz(normalized, 'America/Los_Angeles').toDate();
    return dt;
}
